import logger from '@eventbrite/client-logger';
import {
    client as braintreeClient,
    Client,
    paypalCheckout,
    PayPalCheckout,
} from 'braintree-web';

const PAYPAL_SDK_URL = 'https://www.paypal.com/sdk/js';
export const PAYPAL_CREDIT = 'credit';
export const PAYPAL_PAYLATER = 'paylater';

let braintreeClientInstance: Client | undefined;
let braintreeClientToken: string | null = null;
let paypalCheckoutInstance: PayPalCheckout | null = null;

const setBraintreeClientToken = (token: string) => {
    braintreeClientToken = token;
    return braintreeClientToken;
};

const getBraintreeClientInstance = async () => {
    if (braintreeClientInstance) {
        return braintreeClientInstance;
    }

    // we should not call braintreeClient without a token.
    if (!braintreeClientToken) {
        logger.error(
            '[EB-152450] Attempted to create a braintree client instance without a token',
        );
        return;
    }

    braintreeClientInstance = await braintreeClient.create({
        authorization: braintreeClientToken,
    });

    return braintreeClientInstance;
};

export const getPaypalCheckoutInstance = async () => {
    if (paypalCheckoutInstance) {
        return paypalCheckoutInstance;
    }

    if (!braintreeClientToken) {
        logger.error(
            '[EB-152450] Attempted to create a checkout paypal instance without a token',
        );
        return null;
    }
    const clientInstance = await getBraintreeClientInstance();

    paypalCheckoutInstance = await paypalCheckout.create({
        client: clientInstance,
    });

    return paypalCheckoutInstance;
};

const getScriptSrc = (
    clientId: string,
    amountCurrency: string,
    locale: string | undefined,
) =>
    `${PAYPAL_SDK_URL}?client-id=${clientId}&currency=${amountCurrency}&locale=${locale}&intent=authorize&components=buttons,messages,funding-eligibility&enable-funding=paylater`;

const scriptExists = (source: string) =>
    document.querySelector(`script[src="${source}"]`) !== null;

const loadScript = (
    onScriptLoad: () => void,
    onScriptLoadError: () => void,
    clientId: string,
    amountCurrency: string,
    locale: string | undefined,
) => {
    const source = getScriptSrc(clientId, amountCurrency, locale);

    if (scriptExists(source)) {
        onScriptLoad();
        return;
    }
    const node = document.createElement('script');

    node.src = source;
    node.onload = onScriptLoad;
    node.onerror = onScriptLoadError;

    document.head.appendChild(node);
};

interface PaypalScriptConfig {
    amountCurrency: string;
    braintreeClientToken: string;
    locale: string | undefined;
    onScriptLoad: () => void;
    onScriptLoadError: () => void;
}

export const loadPaypalScript = async (config: PaypalScriptConfig) => {
    const {
        amountCurrency,
        braintreeClientToken: braintreeClientTokenFromState,
        locale,
        onScriptLoad,
        onScriptLoadError,
    } = config;
    try {
        braintreeClientToken = setBraintreeClientToken(
            braintreeClientTokenFromState,
        );
        const clientInstance = await getBraintreeClientInstance();

        if (!clientInstance) {
            throw new Error('Braintree Client instantiation failed');
        }

        const {
            gatewayConfiguration: {
                paypal: { clientId },
            },
        } = clientInstance.getConfiguration();

        loadScript(
            onScriptLoad,
            onScriptLoadError,
            clientId,
            amountCurrency,
            locale,
        );
    } catch (e) {
        onScriptLoadError();
        logger.error('[EB-152450] Error loading Paypal script: ', e);
    }
};
